<script>
import State from './State';
import InputState from './InputState';
import { Stone } from '@/game/Board';
import { deepAssign } from '@/utils';

// The red of the last-move marker: about as clear on a black stone as on a white one.
const LAST_MOVE_RED = '#e0302a';

export default {
  name: 'GameBoardRenderer',

  props: {
    state: { type: State, required: true },
    playMode: { type: String, default: 'quick' },
    inputState: { type: InputState, required: true },
    canvas: { type: HTMLCanvasElement, required: false }
  },

  computed: {
    context() {
      return this.canvas && this.canvas.getContext('2d');
    },

    ui() {
      const r = {
        board: {
          coordinates: 0,
          moveNumbers: 0,
          color: '#FFE6C6',
          padding: 0,
          lastMoveCrossSize: .9,
          lastMoveShape: 'triangle',
          lastMoveColour: 'red',
          fontSize: this.$store.state.isMobile ? .03 : .028,
          coordsFontSize: 0,
          markFontSize: 0,
          markFontBold: false,
          stoneSize: .9,
          infSize: .35,
        },
        px: {}
      };

      deepAssign(r, this.$store.state.ui);

      if(this.context) {
        r.px.size = this.context.canvas.width;
        r.px.coordsFontSize = Math.floor(r.px.size * r.board.fontSize) + r.board.coordsFontSize;
        r.px.markFontSize = Math.floor(r.px.size * r.board.fontSize) + r.board.markFontSize;
        r.px.start = r.px.size * ((this.$store.state.isMobile ? .03 : .05) + r.board.padding * .01) + (r.board.coordinates > 0 ? r.px.coordsFontSize : 0);
        r.px.end = r.px.size - r.px.start;
        r.px.spacing = (r.px.size - r.px.start * 2) / (this.state.grid - 1);
        r.px.markOffY = this.$store.state.isMobile ? 0 : 1;
      }

      return r;
    },
  },

  watch: {
    ui() {
      this.$emit('uiSettings', this.ui);
    }
  },

  render() {
    if(this.context) {
      this.context.rect(0, 0, this.ui.px.size, this.ui.px.size);
      this.context.fillStyle = this.ui.board.color;
      this.context.fill();

      this.paintGrid();
      this.paintStarPoints();
      this.paintCoordinates();
      this.paintCursor();
      this.paintStones();
      this.paintSquares();
      this.paintInfluences();
    }

    return null;
  },

  methods: {
    paintGrid() {
      const c = this.context;

      c.beginPath();
      c.strokeStyle = '#000';
      c.lineWidth = 1;

      for(let i = 0; i < this.state.grid; i++) {
        const x = this.pxInt(i);
        c.moveTo(x, this.ui.px.start);
        c.lineTo(x, this.ui.px.end);

        const y = x;
        c.moveTo(this.ui.px.start, y);
        c.lineTo(this.ui.px.end, y);
      }

      c.stroke();
    },

    paintStarPoints() {
      const c = this.context;
      c.fillStyle = '#000';

      if(this.state.grid >= 13) {
        const coords = [3, this.state.grid - 4];
        if(this.state.grid % 2 === 1) coords.push((this.state.grid - 1) / 2);

        for(const x of coords) {
          for(const y of coords) {
            c.beginPath();
            c.arc(this.pxInt(x), this.pxInt(y), this.ui.px.spacing * 0.1, 0, 2 * Math.PI);
            c.fill();
          }
        }
      }
    },

    paintCoordinates() {
      if(this.ui.board.coordinates === 0) return;

      const c = this.context;
      c.fillStyle = "#000";
      c.font = `${this.ui.px.coordsFontSize}px Arial`;
      const xCoords = "ABCDEFGHJKLMNOPQRSTUVWXYZ";

      for(let x = 0; x < this.state.grid; x++) {
        const xc = xCoords[x % xCoords.length];
        c.textAlign = "center";
        c.textBaseline = "alphabetic";
        c.fillText(xc, this.pxInt(x), this.ui.px.coordsFontSize + 5);

        if(this.ui.board.coordinates >= 2)
          c.fillText(xc, this.pxInt(x), this.ui.px.size - 10);
      }

      for(let y = 0; y < this.state.grid; y++) {
        const yc = this.state.grid - y;
        c.textAlign = "right";
        c.textBaseline = "middle";
        c.fillText(yc, this.ui.px.coordsFontSize + 5, this.pxInt(y));

        if(this.ui.board.coordinates >= 2) {
          c.textAlign = "left";
          c.fillText(yc, this.ui.px.size - this.ui.px.coordsFontSize - 10, this.pxInt(y));
        }
      }
    },

    paintCursor() {
      if(this.state.input.inputType === null)
        return;

      if(this.inputState.cursor) {
        this.paintCross(this.inputState.cursor, this.state.input.validate(this.inputState.cursor) ? '#00F' : '#F00', 2, 'full');
        this.paintInput(this.inputState.cursor, { staged: this.playMode === 'button' });
      }

      if(this.inputState.selected)
        this.paintInput(this.inputState.selected, { staged: true });
    },

    paintStones() {
      const marks = [];
      if(this.state.markChain) {
        for(const k of this.state.markChain.iterate())
          marks.push(k);
      }

      if(this.state.stoneChain) {
        // While every later stone shows a number (a line's or a study's, from 1), the latest stone
        // before them still gets the last-move marker: the move they answer (SPEC §7.3).
        let flagDue = true;
        for(const [i, m] of this.state.stoneChain.iterate()) {
          this.paintStone(m, this.stoneColor(m.stone));
          if(m.by === 'ai') this.paintAiRing(m);
          const marked = this.paintAndRemoveMarkForStone(m, marks);
          // A suggestion's line numbers its stones whatever the setting (SPEC §6.6).
          const numbered = m.index > 0 && (this.state.stoneChain.numbered || this.ui.board.moveNumbers === 'all' || i < this.ui.board.moveNumbers);

          if(!marked && !this.state.infChain) {
            if(numbered)
              this.paintMark(m.x, m.y, i === 0 ? '#F44' : this.stoneColorInverse(m.stone), m.index);
            else if(flagDue && (i === 0 || m.index === 0) && !m.isSetStone)
              this.paintLastMoveFlag(m);
          }
          if(!numbered) flagDue = false;
        }
      }

      this.paintSuggestions(marks);

      const c = this.context;
      for(const k of marks) {
        c.beginPath();
        c.fillStyle = this.ui.board.color;
        c.arc(this.pxInt(k.x), this.pxInt(k.y), this.ui.px.spacing * 0.4, 0, 2 * Math.PI);
        c.fill();
        this.paintMark(k.x, k.y, '#00F', k.mark);
      }

      this.paintActualMove();
    },

    // The stone actually played next, among the suggestions (SPEC §7.5): a ring in its colour (white
    // with a dark edge) the size of a suggestion's disc; on an empty point the grid lines run through it.
    paintActualMove() {
      const a = this.state.actual;
      if(!a || this.state.infChain) return;

      const c = this.context;
      const x = this.pxInt(a.x), y = this.pxInt(a.y);
      const r = this.ui.px.spacing * 0.45;
      const w = Math.max(2, this.ui.px.spacing * 0.1);
      if(a.stone !== Stone.BLACK) {
        c.beginPath();
        c.strokeStyle = '#333';
        c.lineWidth = w + 2;
        c.arc(x, y, r, 0, 2 * Math.PI);
        c.stroke();
      }
      c.beginPath();
      c.strokeStyle = a.stone === Stone.BLACK ? '#111' : '#FFF';
      c.lineWidth = w;
      c.arc(x, y, r, 0, 2 * Math.PI);
      c.stroke();
      c.lineWidth = 1;
    },

    // KataGo's suggested next moves (SPEC §7.5): a blue disc with its rank for the best, light blue
    // for the others. Not drawn under an estimate, nor where a variation letter sits.
    paintSuggestions(marks) {
      if(!this.state.suggestions || this.state.infChain) return;

      const c = this.context;
      for(const s of this.state.suggestions) {
        if(marks.some(k => k.x === s.x && k.y === s.y)) continue;
        c.beginPath();
        c.fillStyle = s.rank === 1 ? '#2a7de1' : '#a9cdf5';
        c.arc(this.pxInt(s.x), this.pxInt(s.y), this.ui.px.spacing * 0.45, 0, 2 * Math.PI);
        c.fill();
        this.paintMark(s.x, s.y, s.rank === 1 ? '#FFF' : '#1b3a5c', s.rank);
      }
    },

    paintAndRemoveMarkForStone(s, marks) {
      for(let i = 0; i < marks.length; i++) {
        const k = marks[i];

        if(k.x === s.x && k.y === s.y) {
          let color = k.color;
          if(!color) color = this.stoneColorInverse(s.stone);
          this.paintMark(k.x, k.y, color, k.mark);
          marks.splice(i, 1);
          return true;
        }
      }

      return false;
    },

    paintInfluences() {
      if(!this.state.infChain) return;

      for(const inf of this.state.infChain.iterate())
        this.paintInfluence(inf.x, inf.y, inf.inf);
    },

    // A territory map's squares (SPEC §7.6): like the estimate's, above the stones, in the colour
    // of who gains the point, or blue / red in the 对比 view; white ones with a dark edge.
    paintSquares() {
      if(!this.state.squares || this.state.infChain) return;

      const c = this.context;
      const r = Math.floor(this.ui.px.spacing * this.ui.board.infSize / 2);
      const fill = { black: '#000', white: '#fff', blue: '#2a7de1', red: '#d0021b' };
      for(const s of this.state.squares) {
        const x = this.pxInt(s.x) - r, y = this.pxInt(s.y) - r;
        c.beginPath();
        c.fillStyle = fill[s.colour] || '#000';
        c.rect(x, y, 2 * r, 2 * r);
        c.fill();
        if(s.colour === 'white') {
          c.strokeStyle = '#333';
          c.lineWidth = 1;
          c.strokeRect(x + 0.5, y + 0.5, 2 * r - 1, 2 * r - 1);
        }
      }
    },

    paintInput(p, options={}) {
      if(this.state.input.inputType === Stone.BLACK || this.state.input.inputType === Stone.WHITE) {
        this.paintStone(p, this.stoneColor(this.state.input.inputType));

        if(options.staged)
          this.paintCross(p, this.stoneColorInverse(this.state.input.inputType), 2, 'small');
      }
    },

    paintStone(p, color) {
      const c = this.context;

      c.beginPath();
      c.strokeStyle = '#000';
      c.lineWidth = 1;
      c.fillStyle = color;
      c.arc(this.pxInt(p.x), this.pxInt(p.y), this.ui.px.spacing * this.ui.board.stoneSize / 2, 0, 2 * Math.PI);
      c.stroke();
      c.fill();
    },

    // A stone the AI played in 试下 (SPEC §6.4): a thin violet ring just outside its edge, a lighter
    // and slightly thicker one on a black stone.
    paintAiRing(m) {
      const c = this.context;
      const black = m.stone === Stone.BLACK;
      c.beginPath();
      c.strokeStyle = black ? '#d2a6f0' : '#8e44ad';
      c.lineWidth = black ? 1.5 : 1;
      c.arc(this.pxInt(m.x), this.pxInt(m.y), this.ui.px.spacing * this.ui.board.stoneSize / 2 + (black ? 2 : 1.5), 0, 2 * Math.PI);
      c.stroke();
      c.lineWidth = 1;
    },

    paintInfluence(x, y, inf) {
      const c = this.context;
      const r = Math.floor(this.ui.px.spacing * this.ui.board.infSize / 2);

      c.beginPath();
      c.fillStyle = (inf > 0 ? "#000" : "#fff");
      c.rect(this.pxInt(x) - r, this.pxInt(y) - r, 2 * r, 2 * r);
      c.fill();
    },

    paintMark(x, y, style, mark) {
      const txt = mark.toString();
      let fontSize = this.ui.px.markFontSize;
      if(txt.length === 1)
        fontSize += 2;
      else if(txt.length > 2)
        fontSize -= 2;

      const c = this.context;
      c.beginPath();
      c.textAlign = "center";
      c.textBaseline = "middle";
      c.fillStyle = style;
      c.font = `${this.ui.board.markFontBold ? 'bold ' : ''}${fontSize}px Arial`;
      c.fillText(mark, this.pxInt(x), this.pxInt(y) + this.ui.px.markOffY);
    },

    paintCross(p, style, width, size) {
      let start, end;

      switch(size) {
        case 'full':
          start = () => this.ui.px.start;
          end = () => this.ui.px.end;
          break;
        case 'small':
          start = c => this.pxInt(c) - Math.floor(this.ui.px.spacing * 0.2);
          end = c => this.pxInt(c) + Math.floor(this.ui.px.spacing * 0.2);
          break;
        default:
          throw new Error(`unknown cross size "${size}"`);
      }

      const c = this.context;
      c.beginPath();
      c.strokeStyle = style;
      c.lineWidth = width;

      c.moveTo(this.pxInt(p.x), start(p.y));
      c.lineTo(this.pxInt(p.x), end(p.y));
      c.moveTo(start(p.x), this.pxInt(p.y));
      c.lineTo(end(p.x), this.pxInt(p.y));
      c.stroke();
    },

    // The last-move marker (SPEC §7.3): a triangle from the stone's centre towards its lower right,
    // or a ring; red, or the stone's opposite colour, as the player chose in the settings.
    paintLastMoveFlag(m) {
      const c = this.context;
      const x = this.pxInt(m.x), y = this.pxInt(m.y);
      const r = this.ui.px.spacing * this.ui.board.stoneSize / 2;
      const colour = this.ui.board.lastMoveColour === 'inverse' ? this.stoneColorInverse(m.stone) : LAST_MOVE_RED;
      c.beginPath();

      if(this.ui.board.lastMoveShape === 'circle') {
        c.strokeStyle = colour;
        c.lineWidth = Math.max(1.5, this.ui.px.spacing * 0.08);
        c.arc(x, y, r * 0.55, 0, 2 * Math.PI);
        c.stroke();
        return;
      }

      const d = r * this.ui.board.lastMoveCrossSize;
      c.fillStyle = colour;
      c.moveTo(x, y);
      c.lineTo(x + d, y);
      c.lineTo(x, y + d);
      c.closePath();
      c.fill();
    },

    stoneColor(stone) {
      return stone === Stone.BLACK ? '#000' : '#FFF';
    },

    stoneColorInverse(stone) {
      return stone === Stone.BLACK ? '#FFF' : '#000';
    },

    pxInt(coord) {
      return this.ui.px.start + this.ui.px.spacing * coord;
    },
  }
}
</script>
